import { cn } from "../../../lib/utils";

/**
 * THE FACILITATOR OS SKELETON. Shimmer, guarded, announced.
 *
 * ===================== THE GUARD IS IN THE PRIMITIVE, NOT THE CALL SITE ====
 * FE-9.1 added `motion-reduce:animate-none` to all five shimmer sites and left a
 * repo-wide AST sweep behind that fails the build if a sixth appears without it.
 * That sweep is the enforcement; this file is the reason a sixth one cannot be
 * written wrong in the first place. The sweep catches the mistake, the primitive
 * removes the opportunity. Both are worth having: the sweep still covers the
 * `animate-shimmer` a future author writes by hand.
 *
 * ===================== `aria-busy` WAS ON THREE OF FIVE ===================
 * A loading region that does not announce itself is a screen reader reading a
 * populated page that is not populated. `SkeletonRegion` carries `role="status"`
 * and `aria-busy="true"` and a visually hidden label, so the announcement is a
 * property of using the primitive rather than of remembering the attribute. The
 * blocks themselves are `aria-hidden`: they are texture, and a screen reader
 * enumerating six grey rectangles is worse than silence.
 *
 * ===================== GEOMETRY IS THE CALLER'S, AND THAT IS THE POINT =====
 * The brief's rule is that a skeleton preserves the eventual layout so the page
 * does not jump. Three of the five sites did not: two bars stood in for a
 * three-section room, and Prep's skeleton sat at `m-8` while its own resolved
 * content sat at `mx-auto max-w-[1180px]`, so the shimmer was not even in the
 * same column as what replaced it. A primitive cannot know a surface's eventual
 * layout, so it takes the geometry as `className` and owns everything else. What
 * it can do, and does, is make the wrong answer visible: a `<Skeleton />` with no
 * geometry is obviously a placeholder for a decision nobody made.
 */

export const Skeleton = ({ className = "", ...rest }) => (
  <span
    aria-hidden="true"
    className={cn(
      "block animate-shimmer motion-reduce:animate-none rounded-md bg-background-cloudburst",
      className,
    )}
    {...rest}
  />
);

/**
 * The announced wrapper. `label` is what a screen reader hears while the region
 * is busy; it is a sentence about this surface, not the word "loading".
 */
export const SkeletonRegion = ({
  label,
  testId,
  className = "",
  children,
  ...rest
}) => (
  <div
    role="status"
    aria-busy="true"
    data-testid={testId}
    className={className}
    {...rest}
  >
    <span className="sr-only">{label}</span>
    {children}
  </div>
);

export default Skeleton;
